import { Modal } from '@mantine/core'
import { Select } from '@forwardflow/ui-v2'
import { motion } from 'framer-motion'
import { useState, useMemo } from 'react'
import { IPhoneNumber } from '../../interface'
import { CloseIcon } from '@forwardflow/icons'

interface AssignToCampaignModalProps {
   isOpen: boolean
   onClose: () => void
   onConfirm: (campaignId: string) => void
   phoneNumber?: IPhoneNumber
   campaigns?: { campaignId: string; campaignName: string }[]
   isBulkMode?: boolean
   bulkCount?: number
   isLoading?: boolean
}

export const AssignToCampaignModal = ({
   isOpen,
   onClose,
   onConfirm,
   phoneNumber,
   campaigns = [],
   isBulkMode = false,
   bulkCount = 0,
   isLoading = false,
}: AssignToCampaignModalProps) => {
   const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(
      null
   )

   const campaignOptions = useMemo(
      () =>
         campaigns.map((campaign) => ({
            value: campaign.campaignId,
            label: campaign.campaignName,
         })),
      [campaigns]
   )

   const handleConfirm = () => {
      if (selectedCampaignId) {
         onConfirm(selectedCampaignId)
         setSelectedCampaignId(null)
      }
   }

   const handleClose = () => {
      if (isLoading) return
      setSelectedCampaignId(null)
      onClose()
   }

   if (!isBulkMode && !phoneNumber) return null

   return (
      <Modal
         opened={isOpen}
         onClose={handleClose}
         withCloseButton={false}
         centered
         size="md"
         padding={0}
         styles={{
            content: {
               backgroundColor: 'rgba(0, 0, 0, 0.50)',
               backdropFilter: 'blur(8px)',
               borderRadius: '12px',
            },
            body: {
               padding: 0,
            },
         }}
      >
         <div className="flex flex-col p-8">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
               <h2 className="text-white text-xl font-medium">
                  Assign to Campaign
               </h2>
               <button
                  onClick={handleClose}
                  className="text-white/70 hover:text-white transition-colors p-1"
               >
                  <CloseIcon className="w-5 h-5" />
               </button>
            </div>

            {/* Content */}
            <div className="mb-6">
               <p className="text-white/90 text-sm mb-4">
                  {isBulkMode
                     ? `Select the Campaign you want to allocate these ${bulkCount} numbers to:`
                     : 'Select the Campaign you want to allocate this number to:'}
               </p>

               {/* Campaign Dropdown */}
               <Select
                  data={campaignOptions}
                  value={selectedCampaignId}
                  onChange={setSelectedCampaignId}
                  placeholder="Select Campaign"
                  searchable
                  clearable
               />
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-y-3">
               <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleConfirm}
                  disabled={!selectedCampaignId || isLoading}
                  className={`w-full px-8 py-3 rounded-lg text-white font-medium transition-colors flex items-center justify-center gap-x-2 ${
                     selectedCampaignId && !isLoading
                        ? 'bg-[#9061DA] hover:bg-[#7E4EC9]'
                        : 'bg-[#9061DA]/40 cursor-not-allowed'
                  }`}
               >
                  {isLoading && (
                     <motion.div
                        animate={{ rotate: 360 }}
                        transition={{
                           duration: 1,
                           repeat: Infinity,
                           ease: 'linear',
                        }}
                        className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                     />
                  )}
                  {isLoading ? 'Allocating...' : 'Allocate'}
               </motion.button>
            </div>
         </div>
      </Modal>
   )
}
